import * as React from 'react';

import { cn } from '@/shared/lib/utils';
import { useExpandableText } from '@/shared/hooks/use-expandable-text';

interface ExpandableTextProps extends Omit<React.ComponentProps<'div'>, 'children'> {
  /** Texto a ser exibido. */
  children: React.ReactNode;
  /** Número de linhas exibidas quando recolhido. Padrão: `2`. */
  collapsedLines?: number;
  /** Rótulo do gatilho quando recolhido. Padrão: `"ver mais"`. */
  expandLabel?: React.ReactNode;
  /** Rótulo do gatilho quando expandido. Padrão: `"ver menos"`. */
  collapseLabel?: React.ReactNode;
  /** Classe aplicada ao elemento de texto (o que recebe o `line-clamp`). */
  textClassName?: string;
  /** Classe aplicada ao botão de gatilho. */
  triggerClassName?: string;
}

/**
 * Texto expansível reutilizável: recorta o conteúdo em `collapsedLines` linhas
 * e exibe um gatilho de "ver mais"/"ver menos" apenas quando o texto realmente
 * transborda. Constrói sobre `useExpandableText`, que faz a detecção de
 * overflow.
 */
export function ExpandableText({
  children,
  collapsedLines = 2,
  expandLabel = 'ver mais',
  collapseLabel = 'ver menos',
  className,
  textClassName,
  triggerClassName,
  ...props
}: ExpandableTextProps) {
  const { ref, expanded, isOverflowing, toggle } = useExpandableText<HTMLParagraphElement>({
    deps: [children, collapsedLines],
  });

  return (
    <div
      data-slot="expandable-text"
      className={cn('flex flex-col items-start', className)}
      {...props}
    >
      <p
        ref={ref}
        className={cn(!expanded && 'overflow-hidden', textClassName)}
        style={
          expanded
            ? undefined
            : {
                display: '-webkit-box',
                WebkitBoxOrient: 'vertical',
                WebkitLineClamp: collapsedLines,
              }
        }
      >
        {children}
      </p>
      {(isOverflowing || expanded) && (
        <button
          type="button"
          onClick={toggle}
          aria-expanded={expanded}
          className={cn('font-semibold text-inherit hover:opacity-80', triggerClassName)}
        >
          {expanded ? collapseLabel : expandLabel}
        </button>
      )}
    </div>
  );
}
